import { useState, type ReactElement } from 'react';

import { apiErrorReason } from '@/lib/api';
import { useDebouncedValue } from '@/lib/use-debounced-value';

import { usePrintLogPeople } from '../use-watermark';
import { errorAdvice } from '@/lib/error-advice';

// Filtro por persona de la bitácora (G1-3, QA MAR-06): busca entre quienes imprimieron o
// descargaron en la empresa activa y deja la elegida como chip, con el mismo patrón que
// el filtro por documento.

interface Props {
  companyId: string;
  /** Id de la persona filtrada (de la URL), o null. */
  value: string | null;
  /** Nombre de la persona filtrada, si se conoce (de sus eventos). */
  name: string | null;
  onChange: (persona: string | null) => void;
}

export function PersonFilterField({ companyId, value, name, onChange }: Props): ReactElement {
  const [q, setQ] = useState('');
  const search = useDebouncedValue(q.trim(), 250);
  const people = usePrintLogPeople(search, { companyId, enabled: value === null });
  const ready = search.length >= 2 && value === null;

  return (
    <div className="fld">
      <span className="fld-lab">Persona</span>
      {value !== null ? (
        <span className="chipsel">
          <span>{name ?? 'Persona seleccionada'}</span>
          <button
            type="button"
            className="btn mut sm"
            aria-label="Quitar filtro de persona"
            onClick={() => onChange(null)}
          >
            Quitar
          </button>
        </span>
      ) : (
        <>
          <input
            type="search"
            className="selector fw"
            aria-label="Buscar persona por nombre"
            placeholder="Nombre"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {ready && people.isFetching && people.data === undefined && !people.isError && (
            <span className="soft" role="status">
              Buscando personas…
            </span>
          )}
          {ready && people.isError && (
            <span className="fld-err" role="alert">
              {apiErrorReason(people.error) === 'limit_exceeded'
                ? 'El registro tiene demasiadas personas para buscar por nombre. Contacta a soporte.'
                : `No se pudo buscar. ${errorAdvice(people.error)}`}
            </span>
          )}
          {ready && people.data?.length === 0 && (
            <span className="soft">Nadie con ese nombre imprimió ni descargó.</span>
          )}
          {ready && (people.data?.length ?? 0) > 0 && (
            <ul className="doc-picker" aria-label="Personas encontradas">
              {people.data?.map((p) => (
                <li key={p.userId}>
                  <button
                    type="button"
                    className="doc-opt"
                    onClick={() => {
                      onChange(p.userId);
                      setQ('');
                    }}
                  >
                    {p.displayName}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
